import { useEffect, useMemo, useRef, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import CustomModal from '../../../components/CustomModal';
import Input from '../../../components/input';
import Select from '../../../components/Select';
import { searchIcon } from '../../../utils/svgs';
import '../../../styles/Issue.css';
import { TransferStockSchema } from '../../../utils/Schema';
import { errorNotify, successNotify } from '../../../utils/toast';
import { createWarehouseStockTransfer, getPartsByWarehouse, getWarehousesDropdown } from '../../../store/Slices/PartVendorSlice';

const formatPartId = (value) => {
    if (value === undefined || value === null || value === '') return '-';
    const normalized = String(value).trim();
    if (!normalized) return '-';
    if (/^P-/i.test(normalized)) return normalized;
    return `P-${normalized}`;
};

const toFiniteNumber = (value) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : null;
};

const firstFiniteNumber = (...values) => {
    for (const value of values) {
        const numericValue = toFiniteNumber(value);
        if (numericValue !== null) return numericValue;
    }
    return null;
};

const resolvePartAvailableQty = (part) => {
    const resolved = firstFiniteNumber(
        part?.part_stock?.available_qty,
        part?.part_stock?.available_stock,
        part?.part_stock?.stock_count,
        part?.available_qty,
        part?.available_stock,
        part?.stock_count,
        part?.quantity,
        part?.qty,
    );

    if (resolved === null || resolved < 0) return 0;
    return resolved;
};

const TransferStockModal = ({ show, onClose, onHide, sourceWarehouseId, sourceWarehouseName, onTransferSuccess }) => {
    const dispatch = useDispatch();

    const {
        warehousesDropdownData,
        getWarehousesDropdownLoading,
        warehousePartsListData,
        getPartsByWarehouseLoading,
        createWarehouseStockTransferLoading
    } = useSelector((state) => state.partVendors);

    const [searchTerm, setSearchTerm] = useState('');
    const lastPartsRequestWarehouseRef = useRef(null);

    const closeHandler = onClose || onHide;

    const currentUser = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('fms-maintenance-details')) || {};
        } catch (e) {
            return {};
        }
    }, []);

    const authorizedOption = useMemo(() => {
        const userId = currentUser?.id || null;
        const userLabel = currentUser?.username || currentUser?.name || currentUser?.email || 'Current User';
        return userId ? { value: userId, label: userLabel, showIcon: true, image: currentUser?.image || '' } : null;
    }, [currentUser]);

    useEffect(() => {
        if (!show) return;
        dispatch(getWarehousesDropdown());
    }, [dispatch, show]);

    useEffect(() => {
        if (show) return;
        setSearchTerm('');
        lastPartsRequestWarehouseRef.current = null;
    }, [show]);

    useEffect(() => {
        if (!show) return;
        if (!sourceWarehouseId) return;
        if (lastPartsRequestWarehouseRef.current === Number(sourceWarehouseId)) return;

        lastPartsRequestWarehouseRef.current = Number(sourceWarehouseId);
        dispatch(getPartsByWarehouse(Number(sourceWarehouseId)));
    }, [dispatch, show, sourceWarehouseId]);

    const destinationOptions = useMemo(() => {
        const sourceId = Number(sourceWarehouseId);
        return (warehousesDropdownData || []).filter((opt) => Number(opt?.value) !== sourceId);
    }, [warehousesDropdownData, sourceWarehouseId]);

    const partsOptions = useMemo(() => {
        const list = warehousePartsListData || [];
        const term = searchTerm.trim().toLowerCase();
        if (!term) return list;

        return list.filter((item) => {
            const idText = formatPartId(item?.id).toLowerCase();
            const nameText = String(item?.name || item?.part_number || item?.description || '').toLowerCase();
            return idText.includes(term) || nameText.includes(term);
        });
    }, [warehousePartsListData, searchTerm]);

    const requiredStar = <span className="required_star">*</span>;

    return (
        <CustomModal
            show={show}
            handleClose={closeHandler}
            heading="Transfer Stock"
            size="lg"
            customHeaderCss="transfer-modal-header"
        >
            <Formik
                key={`${show}-${sourceWarehouseId || ''}`}
                enableReinitialize={true}
                initialValues={{
                    source_warehouse_id: Number(sourceWarehouseId) || '',
                    destination_warehouse: null,
                    authorized_by: authorizedOption,
                    items: []
                }}
                validationSchema={TransferStockSchema}
                onSubmit={async (values, { setSubmitting, resetForm }) => {
                    try {
                        const payload = {
                            from_warehouse_id: Number(values.source_warehouse_id),
                            to_warehouse_id: Number(values.destination_warehouse?.value),
                            items: values.items.map((item) => ({
                                part_id: Number(item.part_id),
                                quantity: Number(item.quantity)
                            }))
                        };

                        await dispatch(createWarehouseStockTransfer(payload)).unwrap();
                        successNotify('Stock transfer initiated successfully');

                        if (typeof onTransferSuccess === 'function') {
                            onTransferSuccess();
                        }

                        resetForm();
                        setSearchTerm('');
                        if (typeof closeHandler === 'function') closeHandler();
                    } catch (error) {
                        errorNotify(error);
                    } finally {
                        setSubmitting(false);
                    }
                }}
            >
                {({ values, errors, touched, handleSubmit, setFieldValue, setFieldTouched, isSubmitting, submitCount }) => {
                    const selectedPartIds = values.items.map((item) => Number(item.part_id));
                    const addableParts = partsOptions.filter((part) => !selectedPartIds.includes(Number(part.id)));

                    const addPart = (part) => {
                        const alreadyAdded = values.items.some((item) => Number(item.part_id) === Number(part.id));
                        if (alreadyAdded) return;

                        const availableQty = resolvePartAvailableQty(part);
                        if (availableQty <= 0) {
                            errorNotify('Selected part is out of stock and cannot be transferred');
                            return;
                        }

                        setFieldValue('items', [
                            ...values.items,
                            {
                                part_id: Number(part.id),
                                part_name: part?.name || part?.part_number || part?.description || '-',
                                quantity: '',
                                available_qty: availableQty
                            }
                        ]);
                        setSearchTerm('');
                    };

                    const removePart = (partId) => {
                        const updated = values.items.filter((item) => Number(item.part_id) !== Number(partId));
                        setFieldValue('items', updated);
                    };

                    return (
                        <form onSubmit={handleSubmit} className="transfer_modal_body">
                            <Row>
                                <Col md={12} className="mb-2">
                                    <Input
                                        label={<>Source Warehouse</>}
                                        placeholder="Source Warehouse"
                                        value={sourceWarehouseName || '-'}
                                        disabled={true}
                                        required={false}
                                    />
                                </Col>

                                <Col md={12} className="mb-2">
                                    <Select
                                        label={<>Destination Warehouse {requiredStar}</>}
                                        placeholder="Select Destination Warehouse"
                                        options={destinationOptions}
                                        loading={getWarehousesDropdownLoading}
                                        value={values.destination_warehouse}
                                        onChange={(val) => setFieldValue('destination_warehouse', val)}
                                        onBlur={() => setFieldTouched('destination_warehouse', true)}
                                        error={errors.destination_warehouse}
                                        touched={touched.destination_warehouse}
                                        menuPortalTarget={document.body}
                                        customStyles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                    />
                                </Col>

                                <Col md={12} className="mb-2">
                                    <div className="transfer_modal_section">
                                        <label>Part Selection</label>
                                        <div className="transfer_modal_search">
                                            <input
                                                type="text"
                                                placeholder="Search Part to Add"
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') e.preventDefault();
                                                }}
                                            />
                                            <span className="transfer_modal_search_icon">{searchIcon}</span>
                                        </div>

                                        {searchTerm.trim() && (
                                            <div
                                                style={{
                                                    border: '1px solid #E3E3E8',
                                                    borderRadius: '8px',
                                                    marginTop: '8px',
                                                    maxHeight: '160px',
                                                    overflowY: 'auto',
                                                    background: '#fff'
                                                }}
                                            >
                                                {addableParts.length > 0 ? addableParts.map((part) => (
                                                    <button
                                                        key={part.id}
                                                        type="button"
                                                        onClick={() => addPart(part)}
                                                        style={{
                                                            width: '100%',
                                                            border: 'none',
                                                            borderBottom: '1px solid #F1F1F5',
                                                            background: 'transparent',
                                                            textAlign: 'left',
                                                            padding: '8px 10px',
                                                            cursor: 'pointer',
                                                            color: '#3D3F5C',
                                                            fontSize: '13px'
                                                        }}
                                                    >
                                                        {formatPartId(part.id)} - {part?.name || part?.part_number || part?.description || '-'}
                                                    </button>
                                                )) : (
                                                    <div style={{ padding: '10px', color: '#9C9FC9', fontSize: '13px' }}>
                                                        {getPartsByWarehouseLoading ? 'Loading parts...' : 'No parts found'}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        <div className="transfer_modal_table">
                                            <div className="transfer_modal_table_head">
                                                <span>Part ID</span>
                                                <span>Part Name</span>
                                                <span>Quantity</span>
                                                <span>Action</span>
                                            </div>
                                            {values.items.length > 0 ? values.items.map((row, index) => (
                                                <div className="transfer_modal_table_row" key={row.part_id}>
                                                    <input type="text" value={formatPartId(row.part_id)} readOnly />
                                                    <input type="text" value={row.part_name} readOnly />
                                                    <div className="transfer_qty_cell">
                                                        <input
                                                            type="number"
                                                            min="1"
                                                            max={row.available_qty || undefined}
                                                            value={row.quantity}
                                                            onChange={(e) => setFieldValue(`items[${index}].quantity`, e.target.value)}
                                                            onBlur={() => setFieldTouched(`items[${index}].quantity`, true)}
                                                        />
                                                        <div style={{ color: '#9C9FC9', fontSize: '11px', marginTop: '2px' }}>
                                                            Available: {row.available_qty}
                                                        </div>
                                                        {(touched?.items?.[index]?.quantity || submitCount > 0) && errors?.items?.[index]?.quantity && (
                                                            <div style={{ color: '#ff0000de', fontSize: '11px', marginTop: '3px' }}>
                                                                {errors.items[index].quantity}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className="transfer_modal_remove_btn"
                                                        onClick={() => removePart(row.part_id)}
                                                        aria-label={`Remove ${row.part_name}`}
                                                    >
                                                        x
                                                    </button>
                                                </div>
                                            )) : (
                                                <div style={{ padding: '12px', color: '#9C9FC9', fontSize: '13px' }}>
                                                    Search and add parts to transfer
                                                </div>
                                            )}
                                        </div>
                                        {typeof errors.items === 'string' && (
                                            <div style={{ color: '#ff0000de', fontSize: '12px', marginTop: '6px' }}>{errors.items}</div>
                                        )}
                                    </div>
                                </Col>

                                <Col md={12} className="mb-2">
                                    <Select
                                        label={<>Authorized By {requiredStar}</>}
                                        placeholder="Authorized By"
                                        options={authorizedOption ? [authorizedOption] : []}
                                        value={values.authorized_by}
                                        onChange={(val) => setFieldValue('authorized_by', val)}
                                        isDisabled={true}
                                        required={false}
                                        menuPortalTarget={document.body}
                                        customStyles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                    />
                                </Col>
                            </Row>

                            <div className="transfer_modal_actions">
                                <button type="button" className="transfer_modal_cancel" onClick={closeHandler}>
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="transfer_modal_submit"
                                    disabled={isSubmitting || createWarehouseStockTransferLoading}
                                >
                                    {isSubmitting || createWarehouseStockTransferLoading ? 'Submitting...' : 'Initiate Transfer'}
                                </button>
                            </div>
                        </form>
                    );
                }}
            </Formik>
        </CustomModal>
    );
};

export default TransferStockModal;
